/**
 * Smart Callback Route Selection Utility
 * Automatically routes between client and platform callback handlers
 * based on authentication context and organization parameters
 */

import { getSubdomain, getCurrentBranchSlug } from './subdomain';
import { stripStatePrefix, buildStateAwarePath } from './tenant';

/**
 * Determine the appropriate callback route based on authentication context
 * @param {URLSearchParams} searchParams - URL search parameters from magic link
 * @param {string} currentPath - Current path being accessed
 * @returns {Object} - Routing decision with target and context
 */
export const determineCallbackRoute = (searchParams, currentPath = stripStatePrefix(window.location.pathname)) => {
  const token = searchParams.get('token');
  const stytchTokenType = searchParams.get('stytch_token_type');
  const slug = searchParams.get('slug');
  const orgId = searchParams.get('organization_id');
  
  // Log the callback routing analysis
  console.log('[CallbackRouter] Analyzing callback context:', {
    token: token ? `${token.substring(0, 20)}...` : null,
    stytchTokenType,
    slug,
    orgId,
    currentPath,
    hostname: window.location.hostname,
    subdomain: getSubdomain()
  });

  // Extract context information
  const subdomain = getSubdomain() || slug;
  const isPlatformPath = currentPath.startsWith('/platform');
  const isClientPath = currentPath.startsWith('/auth/callback');
  
  // Decision logic for callback routing
  const context = {
    subdomain,
    slug,
    orgId,
    token,
    stytchTokenType,
    isPlatformPath,
    isClientPath
  };

  // Platform callback criteria:
  // 1. Currently on platform path (/platform/*) - this is the most reliable indicator
  // 2. Token type with platform context
  // FIXED: Remove hardcoded org IDs - use path-based detection instead
  const shouldUsePlatformCallback = (
    isPlatformPath ||
    (stytchTokenType === 'multi_tenant_magic_links' && !subdomain && !slug) // Platform admin flow
  );

  // Client callback criteria:
  // 1. Has organization context (subdomain or slug parameter) OR standard B2B flow
  // 2. Standard B2B magic link authentication
  // 3. NOT explicitly on platform path
  // CRITICAL FIX: Default to client callback for all B2B authentication flows
  const shouldUseClientCallback = (
    !isPlatformPath && 
    (subdomain || slug || stytchTokenType === 'magic_links' || !stytchTokenType)
  );

  let targetRoute;
  let routeType;
  
  if (shouldUsePlatformCallback) {
    targetRoute = '/platform/auth/callback';
    routeType = 'platform';
  } else if (shouldUseClientCallback) {
    targetRoute = '/auth/callback';
    routeType = 'client';
  } else {
    // Default to client callback for standard B2B flows
    targetRoute = '/auth/callback';
    routeType = 'client';
  }

  const decision = {
    targetRoute,
    routeType,
    context,
    shouldRedirect: currentPath !== targetRoute,
    preservedParams: {
      token,
      stytch_token_type: stytchTokenType,
      slug: slug || subdomain,
      organization_id: orgId
    }
  };

  console.log('[CallbackRouter] Routing decision:', decision);
  
  return decision;
};

/**
 * Construct callback URL with preserved parameters
 * @param {string} baseRoute - Target callback route
 * @param {Object} params - Parameters to preserve
 * @returns {string} - Complete callback URL
 */
export const buildCallbackUrl = (baseRoute, params = {}) => {
  const url = new URL(baseRoute, window.location.origin);
  
  // Add parameters while filtering out null/undefined values
  Object.entries(params).forEach(([key, value]) => {
    if (value) {
      url.searchParams.set(key, value);
    }
  });

  return url.pathname + url.search;
};

/**
 * Execute smart callback routing
 * Redirects to appropriate callback handler if needed
 * @param {URLSearchParams} searchParams - URL search parameters
 * @returns {boolean} - True if redirect occurred, false if staying on current route
 */
export const executeCallbackRouting = (searchParams) => {
  // Early exit if we're already on the correct callback route to prevent loops
  const currentPath = stripStatePrefix(window.location.pathname);
  if (currentPath === '/auth/callback' || currentPath === '/platform/auth/callback') {
    console.log('[CallbackRouter] Already on a callback route, checking if it\'s the correct one');
  }
  
  const decision = determineCallbackRoute(searchParams);
  
  if (decision.shouldRedirect) {
    const targetUrl = buildCallbackUrl(buildStateAwarePath(decision.targetRoute), decision.preservedParams);
    
    console.log('[CallbackRouter] Redirecting to:', targetUrl);
    console.log('[CallbackRouter] Redirect reason:', decision.routeType);
    console.log('[CallbackRouter] From path:', currentPath);
    console.log('[CallbackRouter] To path:', decision.targetRoute);
    
    // Perform the redirect
    window.location.href = targetUrl;
    return true;
  }
  
  console.log('[CallbackRouter] Staying on current route:', decision.targetRoute);
  return false;
};

/**
 * Extract and preserve organization context from magic link
 * @param {URLSearchParams} searchParams - URL search parameters
 * @returns {Object} - Organization context information
 */
export const extractOrganizationContext = (searchParams) => {
  const slug = searchParams.get('slug');
  const orgId = searchParams.get('organization_id');
  const branchParam = searchParams.get('branch') || searchParams.get('branch_slug');
  const subdomain = getSubdomain();
  const branchFromHost = getCurrentBranchSlug();
  
  // Determine the organization identifier
  const organizationSlug = subdomain || slug;
  const branchSlug = branchParam || branchFromHost || null;
  
  const context = {
    slug: organizationSlug,
    orgId,
    subdomain,
    branchSlug,
    hasBranchContext: Boolean(branchSlug),
    hasOrganizationContext: !!(organizationSlug || orgId)
  };

  console.log('[CallbackRouter] Extracted organization context:', context);
  
  return context;
};
